'use client';

import classNames from 'classnames';
import throttle from 'lodash.throttle';
import { LngLat, MapMouseEvent } from 'mapbox-gl';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import { getFormattedElevation } from '@alltrails/data-formatting/distanceFormatters';
import useMap from '../../hooks/useMap';
import styles from './MapCoordinates.module.scss';

type MapCoordinatesProps = {
  className?: string;
  displayMetric: boolean;
};

const MapCoordinates = ({ className, displayMetric }: MapCoordinatesProps) => {
  const intl = useIntl();
  const map = useMap();
  const [lngLat, setLngLat] = useState<LngLat | undefined>(undefined);

  const onMouseMove = useMemo(
    () =>
      throttle((event: MapMouseEvent) => {
        setLngLat(event.lngLat.wrap());
      }, 50),
    []
  );

  useEffect(() => {
    map?.on('mousemove', onMouseMove);

    return () => {
      map?.off('mousemove', onMouseMove);
    };
  }, [map, onMouseMove]);

  const elevation = lngLat ? map?.queryTerrainElevation(lngLat, { exaggerated: false }) : undefined;

  return map && lngLat ? (
    <Typography className={classNames(styles.container, className)} component="div" variant="text100">
      <span>{`${lngLat.lat.toFixed(5)}, ${lngLat.lng.toFixed(5)}`}</span>
      {elevation || elevation === 0 ? (
        <>
          <span>·</span>
          <span className={styles.elevation}>{getFormattedElevation(intl, elevation, displayMetric)}</span>
        </>
      ) : null}
    </Typography>
  ) : null;
};

export default MapCoordinates;
